import { Table, Checkbox, Button, Tooltip } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { modules, entities } from "byzantine";
import {
  BulkTemplatePaymentRowType,
  useCreateBulkTemplateRowActions,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import { useUserFeatures } from "../../../../contexts/UserFeaturesContext";
import { RecipientDrawer, SECTION_NAMES } from "../../../../RecipientDrawer";
import { PostingDialog } from "../../../../PaymentActivity/CreateBulk";
import styles from "../../../../PaymentActivity/CreateBulk/table/BulkPaymentTable.module.scss";
import {
  ActionBar,
  useBulkTableState,
  useRecipientDrawerState,
} from "../../../../PaymentActivity/CreateBulk/table";
import useShowCollectAmounts from "../../../../PaymentActivity/CreateBulk/useShowCollectAmounts";
import PaymentsTableRow from "./PaymentsTableRow";

const PaymentsTable = () => {
  const { l10n } = useLocalization();
  const { expedited_payments: expeditedPaymentsFeature } = useUserFeatures();
  const showSpeedColumn = Boolean(expeditedPaymentsFeature);
  const form = entities.bulkTemplates.BulkTemplateForm.useForm();
  const rowActions = useCreateBulkTemplateRowActions();
  // if user doesn't have manage recipient permissions, just show them recipients they can only send ach payments to
  const recipients = modules.achPayments.usePermissionedRecipients();
  const recipientDrawer = useRecipientDrawerState(recipients, form);

  const {
    checkboxHeaderSelected,
    handleToggleCheckboxHeader,
    addRow,
    selectedRows,
    toggleRowSelection,
    handleClearRowsSelection,
    handleDuplicateRow,
    handleRemoveRows,
    calculateTotalAmount,
    postingDialogIsOpen,
    togglePostingDialog,
    eligibilityCacheRef,
  } = useBulkTableState(rowActions);

  const { values } = form;
  const showCollectAmounts = useShowCollectAmounts(
    values.payments.some(
      (p: BulkTemplatePaymentRowType) => p.transactionType === "collect"
    )
  );

  const COL_VISIBILITY: React.ComponentProps<typeof Table>["colVisibility"] =
    showSpeedColumn
      ? [
          "*", // checkbox
          "*", // recipient
          "*", // action
          "*", // speed
          "*", // description
          "*", // amount
        ]
      : [
          "*", // checkbox
          "*", // recipient
          "*", // action
          "*", // description
          "*", // amount
        ];

  const COL_LAYOUT: React.ComponentProps<typeof Table>["colLayout"] =
    showSpeedColumn
      ? {
          s: "60px 320px 155px 195px 350px 220px",
          m: "60px 320px 155px 195px 350px 220px",
          l: "60px 320px 155px 195px 350px 220px",
        }
      : {
          s: "60px 390px 160px 450px 240px",
          m: "60px 390px 160px 450px 240px",
          l: "60px 390px 160px 450px 240px",
        };

  return (
    <div>
      <div className="margin--bottom--m">
        <div className={styles.headingContainer}>
          <h4 className="fontSize--l nds-sans">
            {l10n.getString("bulk-table-header", null, "Payments")}
          </h4>
          <div className={styles.endRowContainer}>
            {values.groupPosting
              ? l10n.getString("posting-dialog-cta-combined")
              : l10n.getString("posting-dialog-cta-single")}
            <span className="margin--left--xs">
              <Button kind="plain" onClick={togglePostingDialog}>
                {l10n.getString("button-edit")}
              </Button>
            </span>
          </div>
        </div>
      </div>
      <Table
        kind="editable"
        colVisibility={COL_VISIBILITY}
        colLayout={COL_LAYOUT}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              <Checkbox
                onChange={handleToggleCheckboxHeader}
                checked={checkboxHeaderSelected}
              />
            </Table.HeaderCell>
            <Table.HeaderCell>
              {showCollectAmounts
                ? l10n.getString("label-to-from")
                : l10n.getString("ach-payment-recipient-title")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("label-action")}
            </Table.HeaderCell>
            {showSpeedColumn && (
              <Table.HeaderCell>
                <div className={styles.headerWithTooltip}>
                  <span className={styles.headerWithTooltipLabel}>
                    {l10n.getString("speed-label")}
                  </span>
                  <Tooltip
                    maxWidth="300px"
                    text={l10n.getString("speed-tooltip-copy")}
                    wrapperDisplay="flex"
                  >
                    <span className="narmi-icon-info"></span>
                  </Tooltip>
                </div>
              </Table.HeaderCell>
            )}
            <Table.HeaderCell>
              <div className={styles.headerWithTooltip}>
                <span className={styles.headerWithTooltipLabel}>
                  {l10n.getString("label-description-optional")}
                </span>
                <Tooltip
                  maxWidth="300px"
                  side="left"
                  text={l10n.getString("bulk-description-tooltip-copy")}
                  wrapperDisplay="flex"
                >
                  <span className="narmi-icon-info"></span>
                </Tooltip>
              </div>
            </Table.HeaderCell>
            <Table.HeaderCell textAlign={"right" as any}>
              {l10n.getString("label-amount")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {values.payments.map(
            (payment: BulkTemplatePaymentRowType, index: number) => (
              <PaymentsTableRow
                key={`template-row__${index}`}
                payment={payment}
                index={index}
                form={form}
                recipients={recipients}
                selectedRows={selectedRows}
                toggleRowSelection={toggleRowSelection}
                handleClickEditRecipient={
                  recipientDrawer.handleClickEditRecipient
                }
                handleClickAddRecipient={
                  recipientDrawer.handleClickAddRecipient
                }
                setCustomAlertCopy={recipientDrawer.setCustomAlertCopy}
                showSpeedColumn={showSpeedColumn}
                eligibilityCache={eligibilityCacheRef.current}
                showCollectAmounts={showCollectAmounts}
              />
            )
          )}
        </Table.Body>

        <div className={styles.addPaymentButton}>
          <Button
            startIcon="plus"
            label={l10n.getString("label-add-payment")}
            kind="plain"
            onClick={addRow}
          />
        </div>
      </Table>

      <ActionBar
        selectedRows={selectedRows}
        handleClearRowsSelection={handleClearRowsSelection}
        handleDuplicateRow={handleDuplicateRow}
        removeRows={handleRemoveRows}
        totalAmount={calculateTotalAmount(selectedRows, form.values.payments)}
      />

      <RecipientDrawer
        recipientId={recipientDrawer.selectedRecipientId}
        handleClose={recipientDrawer.handleCloseDrawer}
        isOpen={recipientDrawer.isRecipientDrawerOpen}
        fromACHFlow={true}
        drawerType={recipientDrawer.typeOfRecipientDrawer}
        rowIndex={recipientDrawer.selectedRowIndex}
        customAlertCopy={recipientDrawer.customAlertCopy}
        defaultSectionToOpen={
          recipientDrawer.customAlertCopy ? SECTION_NAMES.ach : undefined
        }
      />

      <PostingDialog
        isOpen={postingDialogIsOpen}
        handleClose={togglePostingDialog}
        form={form}
      />
    </div>
  );
};

export default PaymentsTable;
